import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, Phone } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { CONTACT } from "@/data/content";

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking");
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) {
      setState("error");
      return;
    }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (attempts.current >= 8) {
        setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setInfo(data);
          setState("paid");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState("error");
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
  }, [params]);

  return (
    <div data-testid="payment-success-page" className="bg-sand min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 grid place-items-center px-5 pt-40 pb-24">
        <div className="max-w-lg text-center">
          {state === "checking" && (
            <>
              <Loader2 className="w-12 h-12 mx-auto text-forest animate-spin" />
              <h1 className="mt-6 font-serif text-4xl text-forest">Confirming your donation…</h1>
              <p className="mt-3 text-ink2">This only takes a moment. Please don’t close this window.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <div className="mx-auto grid place-items-center w-20 h-20 rounded-full bg-sage/25 text-forest">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h1 data-testid="payment-success-title" className="mt-6 font-serif text-5xl text-forest">Thank you</h1>
              <p className="mt-4 text-lg text-ink2 leading-relaxed">
                Your generous gift{info?.amount ? ` of $${info.amount} CAD` : ""} helps give someone a new beginning.
                A receipt has been sent to your email.
              </p>
              <Button onClick={() => navigate("/")} className="mt-8 rounded-full bg-forest hover:bg-forestdark text-sand px-7 h-12">Back to home</Button>
            </>
          )}
          {(state === "error" || state === "timeout") && (
            <>
              <div className="mx-auto grid place-items-center w-20 h-20 rounded-full bg-terracotta/15 text-terracotta">
                <AlertCircle className="w-10 h-10" />
              </div>
              <h1 className="mt-6 font-serif text-4xl text-forest">
                {state === "timeout" ? "Still processing" : "Something went wrong"}
              </h1>
              <p className="mt-3 text-ink2">
                {state === "timeout"
                  ? "Your payment may still be completing. If you were charged, you'll receive a receipt shortly."
                  : "We couldn't confirm this donation. If you were charged, please contact us and we'll help right away."}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate("/donate")} className="rounded-full bg-forest text-sand px-7 h-12">Try again</Button>
                <a href={CONTACT.phoneHref} className="rounded-full border border-forest text-forest px-7 h-12 flex items-center justify-center gap-2 font-semibold">
                  <Phone className="w-4 h-4" /> {CONTACT.phone}
                </a>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
